"use client";

import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Effect } from "effect";
import { RpcClient } from "@effect/rpc";
import { addonAdminEq, AddonAdminRpc } from "@/lib/effect-query";
import { useIdentity } from "@/lib/providers/identity";
import { dataForThisScope, scopedQueryOptions, useRouteScope } from "@/lib/api/scoped-query";
import { useState, useCallback, useMemo } from "react";
import type { AddonKey } from "@/domain/addon";

/**
 * Query key PREFIXES; each entry is `[...prefix, scope]` (G23).
 *
 * `getAddonRequestSummary` answers `requestService.getRequestSummary(identity.orgId)`
 * where the identity is the one resolved for the admitted scope — so an
 * org-admin of two organisations (a brokerage and an underwriter) was shown the
 * OTHER organisation's pending requests, requester names included, in a banner
 * at the top of every page. Worse than cosmetic: "acknowledge all" then fired
 * against whatever org the header said at click time, which could differ from
 * the org whose rows were on screen.
 *
 * The scope is APPENDED, so both invalidations below still match by prefix.
 */
const addonRequestKeys = {
  summary: ["addon-admin", "request-summary"] as const,
  requests: (status?: string) => ["addon-admin", "requests", status] as const
};

/** Stable identities for the empty cases — both feed `useMemo` below. */
const NO_SUMMARIES: ReadonlyArray<never> = [];
const NO_REQUESTS: ReadonlyArray<never> = [];

/**
 * Hook to drive the addon request banner from RPC data.
 *
 * Uses getAddonRequestSummary for banner data (addon names, pending counts)
 * and getAddonRequests for individual request details (user names per addon).
 * Only returns data for org admins with pending requests.
 */
export function useAddonRequestBanner() {
  const {
    identity
  } = useIdentity();
  const queryClient = useQueryClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const isAdmin = identity?.isAdmin ?? false;
  // The ROUTE names the organisation this banner is about (G23).
  const scope = useRouteScope();

  // Fetch aggregated summary (addon names + pending counts)
  const summaryQuery = useQuery({
    ...addonAdminEq.queryOptions(scopedQueryOptions({
      scope,
      key: addonRequestKeys.summary,
      url: "/api/rpc/addon-admin",
      queryFn: () => Effect.gen(function* () {
        const rpcClient = yield* RpcClient.make(AddonAdminRpc);
        return yield* rpcClient.getAddonRequestSummary();
      }).pipe(Effect.scoped),
      enabled: isAdmin,
      staleTime: 30_000
    })),
    placeholderData: keepPreviousData
  });

  // Fetch individual pending requests (for requester names)
  const requestsQuery = useQuery({
    ...addonAdminEq.queryOptions(scopedQueryOptions({
      scope,
      key: addonRequestKeys.requests("pending"),
      url: "/api/rpc/addon-admin",
      queryFn: () => Effect.gen(function* () {
        const rpcClient_0 = yield* RpcClient.make(AddonAdminRpc);
        return yield* rpcClient_0.getAddonRequests({
          status: "pending"
        });
      }).pipe(Effect.scoped),
      enabled: isAdmin,
      staleTime: 30_000
    })),
    placeholderData: keepPreviousData
  });

  // Bulk acknowledge mutation — acknowledges by addon key for each addon
  const acknowledgeMutation = useMutation(addonAdminEq.mutationOptions({
    mutationKey: ["addon-admin", "acknowledge-all"],
    mutationFn: (addonKeys: readonly AddonKey[]) => Effect.gen(function* () {
      const rpcClient_1 = yield* RpcClient.make(AddonAdminRpc);
      let totalAcknowledged = 0;
      for (const addonKey of addonKeys) {
        const result = yield* rpcClient_1.acknowledgeAddonRequests({
          _tag: "ByAddonKey",
          addonKey
        });
        totalAcknowledged += result.acknowledged;
      }
      return {
        acknowledged: totalAcknowledged
      };
    }).pipe(Effect.scoped),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: addonRequestKeys.summary
      });
      queryClient.invalidateQueries({
        queryKey: addonRequestKeys.requests()
      });
    }
  }));

  // Held across the key flip, never ANSWERED with: showing one organisation's
  // requester names on another organisation's surface is the leak, and the
  // banner's own "acknowledge all" would then fire against the wrong org.
  const summaries = dataForThisScope({
    data: summaryQuery.data,
    isPlaceholderData: summaryQuery.isPlaceholderData
  }) ?? NO_SUMMARIES;
  const requests = dataForThisScope({
    data: requestsQuery.data,
    isPlaceholderData: requestsQuery.isPlaceholderData
  }) ?? NO_REQUESTS;

  // Total pending count across all addons
  const totalCount = useMemo(() => summaries.reduce((sum, s) => sum + s.pendingCount, 0), [summaries]);

  // Group requester names by addon key
  const requestersByAddon = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const req of requests) {
      const existing = map.get(req.addonKey) ?? [];
      existing.push(req.requestedByName);
      map.set(req.addonKey, existing);
    }
    return map;
  }, [requests]);
  const acknowledgeAll = useCallback(() => {
    const addonKeys_0 = summaries.map(s_0 => s_0.addonKey as AddonKey);
    if (addonKeys_0.length > 0) {
      acknowledgeMutation.mutate(addonKeys_0);
    }
    setShowConfirmModal(false);
  }, [summaries, acknowledgeMutation]);
  const openConfirmModal = useCallback(() => {
    setShowConfirmModal(true);
  }, []);
  const closeConfirmModal = useCallback(() => {
    setShowConfirmModal(false);
  }, []);
  return {
    /** Aggregated summaries per addon (name, pending count) */
    summaries,
    /** Individual pending requests with requester details */
    requests,
    /** Total pending request count across all addons */
    totalCount,
    /** Requester names grouped by addon key */
    requestersByAddon,
    /** Whether data is loading */
    isLoading: summaryQuery.isLoading || requestsQuery.isLoading || summaryQuery.isPlaceholderData || requestsQuery.isPlaceholderData,
    /** Whether there are any pending requests to show */
    hasRequests: isAdmin && totalCount > 0,
    /** Acknowledge all pending requests (bulk by addon key) */
    acknowledgeAll,
    /** Whether the acknowledge mutation is in progress */
    isAcknowledging: acknowledgeMutation.isPending,
    /** Confirmation modal state */
    showConfirmModal,
    openConfirmModal,
    closeConfirmModal
  };
}